<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  export let currentPage: number = 1;
  export let totalPages: number = 1;
  export let maxVisiblePages: number = 7;

  const dispatch = createEventDispatcher();

  $: {
    // Ensure currentPage is valid
    if (currentPage < 1) currentPage = 1;
    if (totalPages > 0 && currentPage > totalPages) currentPage = totalPages;
  }

  $: visiblePages = getVisiblePages(currentPage, totalPages, maxVisiblePages);

  function getVisiblePages(current: number, total: number, max: number): number[] {
    
    if (total <= max) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // Always show first and last page
    const pages: number[] = [];
    const sidePages = Math.floor((max - 2) / 2);
    
    let startPage = Math.max(2, current - sidePages);
    let endPage = Math.min(total - 1, current + sidePages);
    
    // Adjust if we're near the start
    if (current - sidePages < 2) {
      endPage = Math.min(total - 1, max - 1);
    }
    
    // Adjust if we're near the end
    if (current + sidePages > total - 1) {
      startPage = Math.max(2, total - max + 2);
    }
    
    // Add first page
    pages.push(1);
    
    // Add ellipsis if needed
    if (startPage > 2) {
      pages.push(-1); // -1 represents ellipsis
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis if needed
    if (endPage < total - 1) {
      pages.push(-1); // -1 represents ellipsis
    }
    
    // Add last page
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  }

  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      dispatch('pageChange', { page });
    }
  }
</script>

<div class="pagination">
  <button 
    class="pagination-btn prev" 
    on:click={() => goToPage(currentPage - 1)} 
    disabled={currentPage <= 1}
  >
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
    </svg>
  </button>
  <div class="pagination-container">
  {#each visiblePages as page}
    {#if page === -1}
      <span class="pagination-ellipsis">...</span>
    {:else}
      <button 
        class="pagination-btn page-num {currentPage === page ? 'active' : ''}" 
        on:click={() => goToPage(page)}
      >
        {page}
      </button>
    {/if}
  {/each}
  </div>
  <button 
    class="pagination-btn next" 
    on:click={() => goToPage(currentPage + 1)} 
    disabled={currentPage >= totalPages}
  >
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
    </svg>
  </button>
</div>

<style>
  .pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    /* gap: 0.9rem; */
  }
  .pagination-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 0.25rem;
  }

  .pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid #e5e7eb;
    background: white;
    color: #9ca3af;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 400;
    transition: all 0.2s;
  }

  .pagination-btn:hover:not(:disabled) {
    border-color: #d1d5db;
    color: #6b7280;
  }
  
  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pagination-btn.active {
    border-color: #666;
    color: #374151;
    font-weight: 500;
  }
  
  .pagination-btn.prev,
  .pagination-btn.next {
    width: 32px;
    padding: 0;
  }

  .pagination-ellipsis {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: #6b7280;
  }
  
  @media (max-width: 640px) {
    .pagination-btn {
      width: 28px;
      height: 28px;
      font-size: 0.8rem;
    }
    
    .pagination-ellipsis {
      width: 28px;
      height: 28px;
    }
  }
</style> 